import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import {
  changeSymbolstoDash,
  makeSlug,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import {
  JourneyFormSchema,
  JourneyFormTypes,
} from "@utils/interface/form.schema";
import { IndividualQuery, METHODS } from "@utils/interface/index.interface";
import { JourneyData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
interface FormProps {
  refetch: () => void;
}
const JourneyForm: React.FC<FormProps> = () => {
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("modal-item");
  const modalMode = searchParams.get("modal");

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<JourneyFormTypes>({
    mode: "onChange",
    resolver: zodResolver(JourneyFormSchema),
    defaultValues: {
      title: modalMode === "add" ? (searchParams.get("search") as string) : "",
    },
  });
  useEffect(() => {
    if (modalMode === "add") {
      setValue("slug", changeSymbolstoDash(makeSlug(watch("title"))));
    }
  }, [watch("title")]);
  console.log("🚀 ~ errors:", errors);

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<JourneyData>
  >({
    endpoint: endpoint["journey"],
    key: `${pathname}-${modalItem}`,
    enabled: modalItem ? true : false,
    id: modalItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      console.log(firstResult);
      if (firstResult) {
        if (modalMode == "add") {
          return reset({});
        } else {
          reset(() => ({
            section: [
              {
                label: firstResult?.section || "",
                value: firstResult?.section || "",
              },
            ],
            title: firstResult?.title || "",
            image: firstResult?.image || "",
            header_code: firstResult?.header_code || "",
            footer_code: firstResult?.footer_code || "",
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const handleMutate = (data: any) => {
    data = {
      ...data,
      section: data["section"][0]["value"],
    };
    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
      // if (!(data.image instanceof File || data.image instanceof Blob))
      //   delete data.image;
    }
    return {
      data,
      endpoint: endpoint["journey"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: JourneyData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      queryClient.invalidateQueries({ queryKey: ["journey-section-listing"] });

      showToast({
        description: `Journey ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };

  const { mutate, isPending } = usePostData();
  console.log("errors", errors);
  const onSubmit = (data: JourneyFormTypes) => {
    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema = React.useMemo(
    () => [
      {
        name: "title",
        label: "Title",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Title",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "slug",
        label: "Slug",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Slug",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "section",
        type: "async",
        isMulti: false,
        label: "Select Section",
        placeholder: "Select Section",
        colSpan: 12,
        control: control,
        option: [
          { label: "clarity", value: "clarity" },
          { label: "learning", value: "learning" },
          { label: "mentor", value: "mentor" },
          { label: "achieve", value: "achieve" },
        ],
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "image",
        label: "Upload Image",
        colSpan: 12,
        type: "cropper",
        cropperWidthPlaceholder: 800,
        cropperHeightPlaceholder: 1180,
        cropperWidth: 200,
        cropperHeight: 200,
        upload_type: "image",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
       {
      name: "header_code",
      label: "Header Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "footer_code",
      label: "Footer Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
      {
        type: "submit",
        value:
          isPending || (fetchPending && modalItem)
            ? `processing...`
            : `${modalMode || ""} ${(type || pathname)?.replace("-", " ")}`,
        className: "w-full h-[47px] mt-[32px] capitalize",
        colSpan: 12,
        variant: "default",
        endIcon: isPending ? <Spinner /> : null,
        disabled: isPending ? true : false,
        visable: true,
      },
    ],
    [setValue, control, errors, isPending]
  );

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default JourneyForm;
